import LogoBlack from "@/assets/images/beone-supercharged-logo-black.svg";
import LogoWhite from "@/assets/images/beone-supercharged-logo-white.svg";
import { requestEmail } from "@/models/auth.model";
import { useTheme } from "@/shadcn/components/theme-provider";
import { Button } from "@/shadcn/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shadcn/components/ui/card";
import { Input } from "@/shadcn/components/ui/input";
import { isEmailValid } from "@/utils/InputTesting.util";
import { encodeEmailMl } from "@/utils/encoding.util";
import React, { ChangeEvent, useEffect, useState } from "react";

type MagicLinkProps = {
  handleEmailChange: (e: ChangeEvent<HTMLInputElement>) => void;
  email: string;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  magicLinkSent: boolean;
  setMagicLinkSent: React.Dispatch<React.SetStateAction<boolean>>;
};

const MagicLink: React.FC<MagicLinkProps> = ({
  handleEmailChange,
  email,
  magicLinkSent,
  setMagicLinkSent,
}) => {
  const { theme } = useTheme();
  // const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // typescript boolean useState
  const [busySubmitting, setBusySubmitting] = useState(false);

  const [loginCount, setLoginCount] = useState(0);
  const [loginCoolDown, setLoginCoolDown] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const setError = (message: string) => {
    setErrorMessage(message);
  };

  const clearError = () => {
    setErrorMessage("");
  };

  useEffect(() => {
    if (loginCount === 3) {
      // Start with 10 seconds countdown
      setCountdown(10);
      setLoginCoolDown(true);
    } else if (loginCount > 3) {
      // Start with 60 seconds countdown
      setCountdown(60);
      setLoginCoolDown(true);
    } else {
      setLoginCoolDown(false);
    }
  }, [loginCount]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      // Decrement countdown every second
      timer = setInterval(() => {
        setCountdown((c) => c - 1);
      }, 1000);
    } else {
      setLoginCoolDown(false);
    }

    // Cleanup function to clear the interval
    return () => clearInterval(timer);
  }, [countdown]);

  const handleLogin = async () => {
    // console.log("handleLogin");
    if (busySubmitting) return;
    // if email is different than regex, set error message and return
    const isEmailValidCheck = await isEmailValid(email);

    if (isEmailValidCheck === false) {
      setError("Please enter a valid email address.");
      return;
    }

    setBusySubmitting(true);

    // Encode the email and make the request
    const encodedEmail = await encodeEmailMl(email);
    if (encodedEmail) {
      clearError();
      const emailResponse = await requestEmail(encodedEmail);

      setBusySubmitting(false);

      if (emailResponse === 202) {
        setMagicLinkSent(true);
        return;
      }

      setMagicLinkSent(false);
      setLoginCount((prevCount) => prevCount + 1);

      if (emailResponse === 400) {
        console.log("bad request");
        setError("400 please contact support.");
        return;
      }

      if (emailResponse === 401 || emailResponse === 403) {
        console.log("unauthorized");
        setBusySubmitting(false);
        setError("Unauthorized. Please check your email.");
        return;
      }

      if (emailResponse === 404) {
        console.log("route not found");
        setError("Route not found. Please contact support.");
        return;
      }

      if (emailResponse === 0) {
        console.log("network error");
        setError("Network error. Please try again.");
        return;
      }
      console.log("login failed");
      setError("Server error. Please try again.");
    }

    if ("caches" in window) {
      // console.log(caches)
      caches.keys().then((cacheNames) => {
        cacheNames.forEach((cacheName) => {
          caches.delete(cacheName);
        });
      });
    }

    // Unregister the service worker
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          registration.unregister();
        });
      });
    }
  };

  // const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
  //     setEmail(e.target.value);
  // };

  // Submit if enter key is pressed
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !loginCoolDown) {
      handleLogin();
    }
  };

  // This will handle the back button click after magicLink submit button
  function handleBack() {
    setBusySubmitting(false);
    setMagicLinkSent(false);
  }
  return (
    <div className="flex justify-center align-middle h-screen items-center">
      <Card className="bg-white dark:bg-slate-900 bg-opacity-80 dark:bg-opacity-90 border-none shadow-lg max-w-lg m-auto p-3 px-12 rounded-sm">
        {!magicLinkSent ? (
          <>
            <CardHeader>
              {/* <CardTitle className="text-center font-bold text-2xl">
                Login
              </CardTitle> */}
              <img
                id="login_background_image"
                src={theme === "light" ? LogoBlack : LogoWhite}
                alt="Logo"
                className="object-contain w-full"
              />
            </CardHeader>
            <CardContent className="m-auto text-center">
              <div className="mb-5 flex items-center justify-center">
                <label className="font-bold mr-2 self-center">Email</label>
                <Input
                  type="email"
                  className="form-control rounded-md p-1 ps-2 bg-opacity-100 bg-white dark:bg-slate-800 dark:text-white align-middle"
                  id="email"
                  placeholder="Enter email"
                  value={email}
                  onChange={handleEmailChange}
                  onKeyDown={handleKeyDown}
                  maxLength={255}
                />
              </div>
              {errorMessage ? (
                <div
                  id="magic-link-error-message"
                  className="bg-red-600 p-1 mb-2 rounded opacity-95"
                >
                  {errorMessage}
                </div>
              ) : (
                <></>
              )}
              <Button
                id="magic-link-submit-button"
                type="button"
                className="btn btn-primary rounded-sm"
                onClick={handleLogin}
                disabled={loginCoolDown || busySubmitting}
              >
                {loginCoolDown
                  ? `Login disabled for ${countdown} seconds`
                  : busySubmitting
                    ? "Submitting"
                    : "Submit"}
              </Button>
            </CardContent>
          </>
        ) : (
          <>
            <CardHeader>
              <CardTitle className="text-center font-bold mt-6">
                Magic Link Sent
              </CardTitle>
              <CardContent className="m-auto text-center">
                <p id="magic_link_sent_message" className="mb-3">
                  A magic link has been sent to your email. Please click on the
                  link to login.
                </p>
                <Button
                  id="magic_link-back-button"
                  type="button"
                  className="btn btn-primary"
                  onClick={handleBack}
                >
                  Back
                </Button>
              </CardContent>
            </CardHeader>
          </>
        )}
      </Card>
    </div>
  );
};

export default MagicLink;
